import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import api from '../services/api';
import { RootState } from '../redux/store';

interface CyberProfile {
  id: number;
  username: string;
  email: string;
  nom: string;
  prenom: string;
  telephone: string;
  nom_cyber: string | null;
  adresse_cyber: string | null;
  ville_cyber: string | null;
  code_postal_cyber: string | null;
  telephone_cyber: string | null;
  email_cyber: string | null;
  horaires_cyber: string | null;
  site_web_cyber: string | null;
  description_cyber: string | null;
}

const CyberProfile: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [profile, setProfile] = useState<CyberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/cyber-profile');
      if (response.data.success) {
        setProfile(response.data.profile);
      }
    } catch (err: any) {
      console.error('Erreur chargement profil:', err);
      setError('Impossible de charger le profil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CyberProfile, value: string) => {
    if (profile) {
      setProfile({
        ...profile,
        [field]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      const response = await api.put('/cyber-profile', {
        nom: profile.nom,
        prenom: profile.prenom,
        telephone: profile.telephone,
        nom_cyber: profile.nom_cyber,
        adresse_cyber: profile.adresse_cyber,
        ville_cyber: profile.ville_cyber,
        code_postal_cyber: profile.code_postal_cyber,
        telephone_cyber: profile.telephone_cyber,
        email_cyber: profile.email_cyber,
        horaires_cyber: profile.horaires_cyber,
        site_web_cyber: profile.site_web_cyber,
        description_cyber: profile.description_cyber
      });

      if (response.data.success) {
        setSuccess(true);
        setProfile(response.data.profile);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Erreur mise à jour profil:', err);
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">Impossible de charger le profil</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Mon Profil & Informations de l'Entreprise
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Profil mis à jour avec succès !
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Informations personnelles */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <PersonIcon color="primary" />
                <Typography variant="h6">
                  Informations Personnelles
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Prénom"
                    value={profile.prenom || ''}
                    onChange={(e) => handleChange('prenom', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom"
                    value={profile.nom || ''}
                    onChange={(e) => handleChange('nom', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile.email || ''}
                    disabled
                    helperText="L'email ne peut pas être modifié"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Téléphone personnel"
                    value={profile.telephone || ''}
                    onChange={(e) => handleChange('telephone', e.target.value)}
                    placeholder="+262 269 XX XX XX"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Informations de l'entreprise */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">
                  Informations de l'Entreprise
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Ces informations seront visibles par vos clients dans leur espace PWA.
                Elles leur permettront de vous contacter facilement.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nom de l'entreprise / Bureau"
                    value={profile.nom_cyber || ''}
                    onChange={(e) => handleChange('nom_cyber', e.target.value)}
                    placeholder="Ex: Cyber Services Mayotte"
                    helperText="Le nom qui apparaîtra sur l'espace client"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse complète"
                    value={profile.adresse_cyber || ''}
                    onChange={(e) => handleChange('adresse_cyber', e.target.value)}
                    placeholder="Ex: 15 Rue de la République"
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Ville"
                    value={profile.ville_cyber || ''}
                    onChange={(e) => handleChange('ville_cyber', e.target.value)}
                    placeholder="Ex: Mamoudzou"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Code postal"
                    value={profile.code_postal_cyber || ''}
                    onChange={(e) => handleChange('code_postal_cyber', e.target.value)}
                    placeholder="Ex: 97600"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Téléphone du cyber"
                    value={profile.telephone_cyber || ''}
                    onChange={(e) => handleChange('telephone_cyber', e.target.value)}
                    placeholder="+262 269 XX XX XX"
                    helperText="Téléphone principal pour vos clients"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email du cyber"
                    value={profile.email_cyber || ''}
                    onChange={(e) => handleChange('email_cyber', e.target.value)}
                    type="email"
                    placeholder="contact@votrecyber.com"
                    helperText="Email de contact pour vos clients"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Site web"
                    value={profile.site_web_cyber || ''}
                    onChange={(e) => handleChange('site_web_cyber', e.target.value)}
                    placeholder="https://votresite.com"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Horaires d'ouverture"
                    value={profile.horaires_cyber || ''}
                    onChange={(e) => handleChange('horaires_cyber', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Ex: Lundi - Vendredi: 8h - 17h&#10;Samedi: 9h - 12h"
                    helperText="Appuyez sur Entrée pour passer à la ligne"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description / Présentation"
                    value={profile.description_cyber || ''}
                    onChange={(e) => handleChange('description_cyber', e.target.value)}
                    multiline
                    rows={4}
                    placeholder="Ex: Spécialiste des démarches administratives depuis 10 ans. Accompagnement personnalisé pour vos titres de séjour, cartes d'identité, passeports..."
                    helperText="Une courte présentation de vos services"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Boutons d'action */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </form>

        {/* Aperçu pour les clients */}
        <Box mt={4}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Aperçu de votre carte (vue client)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Voici comment vos informations apparaîtront dans l'espace PWA de vos clients :
          </Typography>

          <Card
            sx={{
              mt: 2,
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white'
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <BusinessIcon fontSize="large" />
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {profile.nom_cyber || 'Nom du cyber non renseigné'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Votre gestionnaire de dossiers
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon fontSize="small" />
                <Typography variant="body2">
                  <strong>Responsable :</strong> {profile.prenom} {profile.nom}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2">
                  📞 {profile.telephone_cyber || profile.telephone || 'Non renseigné'}
                </Typography>
                <Typography variant="body2">
                  ✉️ {profile.email_cyber || profile.email}
                </Typography>
              </Box>

              {profile.adresse_cyber && (
                <Box mt={2} pt={2} sx={{ borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                  <Typography variant="body2">
                    📍 {profile.adresse_cyber}
                    {profile.ville_cyber && `, ${profile.code_postal_cyber || ''} ${profile.ville_cyber}`}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Layout>
  );
};

export default CyberProfile;
